/**
 * Manager Dashboard Hook
 *
 * Supplies the key team-insight metrics shown on the Manager Dashboard.
 *
 * Metrics:
 *   - Team Headcount — the number of people the current manager is responsible
 *     for. This is derived from the Dynamics 365 `EssWorkerManagerRelations`
 *     entity (via `useManagerStatus`), which returns one relation record per
 *     managed person. Reusing that hook means no duplicate API call — react-query
 *     serves both consumers from the same cached query.
 *   - New Joiners (last 15 days) — team members whose EmploymentStartDate on
 *     `/data/EmployeesV2` falls within the last 15 days (via `useNewJoiners`).
 *   - Exiting Employees (next 30 days) — team members whose EmploymentEndDate on
 *     `/data/EmployeesV2` falls within the next 30 days (via `useExitingEmployees`).
 *   - Birthdays this month — team members whose BirthDate on `/data/EmployeesV2`
 *     falls in the current calendar month (via `useTeamBirthdays`).
 *   - On Leave Today — team members with an approved leave request covering
 *     today (via `useOnLeaveToday`, source /data/LeaveRequestsV2).
 *   - Staff on Leave (in next 30 days) — approved team leaves starting within the
 *     next 30 days (via `useUpcomingLeaves`, source /data/LeaveRequestsV2).
 *
 * All dashboard metrics are now API-backed; this hook is the single data entry
 * point that aggregates them.
 *
 * Returns:
 *   - metrics: { headcount, newJoiners, exitingEmployees, birthdaysThisMonth,
 *                onLeaveToday, upcomingLeaves }
 *   - isLoading: boolean
 *   - error: Error | null
 */
import useManagerStatus from "./useManagerStatus";
import useNewJoiners from "./useNewJoiners";
import useExitingEmployees from "./useExitingEmployees";
import useTeamBirthdays from "./useTeamBirthdays";
import useOnLeaveToday from "./useOnLeaveToday";
import useUpcomingLeaves from "./useUpcomingLeaves";

export default function useManagerDashboard() {
  const {
    managedPersonnelNumbers,
    isLoading: statusLoading,
    error: statusError,
  } = useManagerStatus();

  const {
    count: newJoinersCount,
    isLoading: newJoinersLoading,
    error: newJoinersError,
  } = useNewJoiners();

  const {
    count: exitingCount,
    isLoading: exitingLoading,
    error: exitingError,
  } = useExitingEmployees();

  const {
    count: birthdaysCount,
    isLoading: birthdaysLoading,
    error: birthdaysError,
  } = useTeamBirthdays();

  const {
    count: onLeaveTodayCount,
    isLoading: onLeaveLoading,
    error: onLeaveError,
  } = useOnLeaveToday();

  const {
    count: upcomingLeavesCount,
    isLoading: upcomingLoading,
    error: upcomingError,
  } = useUpcomingLeaves();

  return {
    metrics: {
      // Team Headcount — source: /data/EssWorkerManagerRelations
      headcount: managedPersonnelNumbers.length,
      // New Joiners (last 15 days) — source: /data/EmployeesV2 (EmploymentStartDate)
      newJoiners: newJoinersCount,
      // Exiting Employees (next 30 days) — source: /data/EmployeesV2 (EmploymentEndDate)
      exitingEmployees: exitingCount,
      // Birthdays this month — source: /data/EmployeesV2 (BirthDate)
      birthdaysThisMonth: birthdaysCount,
      // On Leave Today — source: LeaveRequestsV2 + EssLeaveRequestHeaders
      onLeaveToday: onLeaveTodayCount,
      // Staff on Leave (in next 30 days) — source: LeaveRequestsV2 + EssLeaveRequestHeaders
      upcomingLeaves: upcomingLeavesCount,
    },
    isLoading:
      statusLoading ||
      newJoinersLoading ||
      exitingLoading ||
      birthdaysLoading ||
      onLeaveLoading ||
      upcomingLoading,
    error:
      statusError ||
      newJoinersError ||
      exitingError ||
      birthdaysError ||
      onLeaveError ||
      upcomingError ||
      null,
  };
}
